import Close from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TERMS_CONTENT, TERMS_TITLE } from '../content/termsOfService';
import { colors } from '../theme';

interface TermsAcceptanceCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: boolean;
  disabled?: boolean;
}

const TermsAcceptanceCheckbox: React.FC<TermsAcceptanceCheckboxProps> = ({
  checked,
  onChange,
  error = false,
  disabled = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const openFullPage = () => {
    setDialogOpen(false);
    navigate('/terms');
  };

  const openTerms = (e: React.MouseEvent) => {
    e.preventDefault();
    setDialogOpen(true);
  };

  return (
    <>
      <Box sx={{ mt: 1.5, mb: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            m: 0,
            cursor: disabled ? 'default' : 'pointer',
            overflow: 'visible',
            '&:hover': { backgroundColor: 'transparent' },
          }}
          onClick={() => !disabled && onChange(!checked)}
        >
          <Checkbox
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            sx={{
              p: 0.5,
              color: error ? 'error.main' : colors.primary.main,
              '&.Mui-checked': { color: colors.primary.main },
            }}
          />
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: '#455a64',
              fontSize: '0.875rem',
              lineHeight: 1.5,
              flex: 1,
            }}
          >
            <Link
              type="button"
              onClick={openTerms}
              sx={{
                color: colors.primary.main,
                fontWeight: 500,
                fontSize: 'inherit',
                textDecoration: 'none',
                cursor: 'pointer',
                p: 0,
                m: 0,
                border: 'none',
                backgroundColor: 'transparent',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Kullanım şartlarını
            </Link>
            {' okudum, kabul ediyorum.'}
          </Typography>
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 6 }}>
            Devam etmek için kullanım şartlarını kabul etmelisiniz.
          </Typography>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e2e8f0',
            pb: 2,
            fontWeight: 700,
            fontSize: '1.125rem',
          }}
        >
          {TERMS_TITLE}
          <IconButton
            aria-label="Kapat"
            onClick={() => setDialogOpen(false)}
            sx={{ color: '#607D8B' ,width:'fit-content'}}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box
            sx={{
              '& h2': { fontSize: '1rem', fontWeight: 700, mt: 2, mb: 1, color: '#263238' },
              '& p': { fontSize: '0.875rem', lineHeight: 1.7, color: '#455a64', mb: 1 },
            }}
          >
            {TERMS_CONTENT.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return (
                  <Typography key={i} component="h2" variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                    {line.replace(/^##\s*/, '')}
                  </Typography>
                );
              }
              if (line.trim() === '---') return <Box key={i} sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />;
              if (line.trim()) {
                return (
                  <Typography key={i} component="p" variant="body2" sx={{ mb: 0.75, lineHeight: 1.6 }}>
                    {line}
                  </Typography>
                );
              }
              return null;
            })}
          </Box>
          <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={openFullPage}
              sx={{
                color: colors.primary.main,
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
              }}
            >
              Tam metni yeni sayfada aç
            </Link>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TermsAcceptanceCheckbox;
